import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  IconButton,
  Grid,
  styled,
} from "@mui/material";
import NorthEastIcon from "@mui/icons-material/NorthEast";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import JobApplicationModal from "./JobApplicationModal";
import PlaceIcon from '@mui/icons-material/Place';
import { formatText } from "../../helper";
import axios from "axios";
import JobDetailsModal from "./JobViewModel";

const ORG_ID = "4";
const BASE_URL = "https://one.ienergydigital.com/oneapp-backend";
const ITEMS_PER_PAGE = 6;

const OuterGlassBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8),
  borderRadius: "24px",
  background:
    "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0.3) 0%, rgba(110, 191, 244, 0.1) 77.08%, rgba(70, 144, 213, 0.2) 100%)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  position: "relative",
  overflow: "hidden",
  minHeight: "600px",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.1)",
  width: "100%",
  margin: "0 auto",

  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
    minHeight: "auto",
    marginBottom: 10,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(4),
    margin: theme.spacing(2, "auto"),
  },
}));

const JobCardStyled = styled(Card)(({ isHovered }) => ({
  background: isHovered
    ? "linear-gradient(262.41deg, #1DA1F2 -8.32%, #6843EC 93.3%)"
    : "radial-gradient(90.16% 143.01% at 15.32% 21.04%, rgba(165, 239, 255, 0) 0%, rgba(110, 191, 244, 0.0247917) 77.08%, rgba(70, 144, 213, 0) 100%)",
  border: isHovered ? "1px solid #07329F" : "1px solid #fff",
  padding: "16px",
  height: "100%",
  transition: "all 0.3s ease",
  cursor: "pointer",
  boxSizing: "border-box",
  borderRadius: "24px",
  backdropFilter: "blur(10px)",
  position: "relative",
  overflow: "hidden",
  boxShadow: "0 4px 24px rgba(0, 0, 0, 0.1)",
}));

// Filter button styling
const FilterButton = styled(Button)(({ isActive }) => ({
  borderRadius: "50px",
  padding: "8px 16px",
  minWidth: "80px",
  color: isActive ? "#07329F" : "#FFFFFF",
  backgroundColor: isActive ? "#61D1FA" : "transparent",
  border: isActive ? "none" : "1px solid rgba(255, 255, 255, 0.3)",
  "&:hover": {
    backgroundColor: isActive ? "#61D1FA" : "rgba(255, 255, 255, 0.1)",
  },
  textTransform: "none",
  margin: "0 4px",
  "@media (max-width: 600px)": {
    minWidth: "auto",
    fontSize: "12px",
    padding: "6px 12px",
  },
}));

// Pagination dots
const PaginationDot = styled(Box)(({ isActive }) => ({
  width: isActive ? "24px" : "8px",
  height: "8px",
  borderRadius: "4px",
  backgroundColor: isActive ? "#61D1FA" : "#FFFFFF",
  margin: "0 4px",
  transition: "all 0.3s ease",
}));

const ApplyNowButton = styled(Button)({
  color: "#FFFFFF",
  textTransform: "none",
  padding: "8px 12px",
});

const JobsList = () => {
  const [allJobs, setAllJobs] = useState([]); // raw items from the API
  const [categories, setCategories] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [activePage, setActivePage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalOpen1, setModalOpen1] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getJobPostings();
  }, []);

  const getJobPostings = async () => {
    setLoading(true);
    try {
      const url = `${BASE_URL}/public/recruitment/${ORG_ID}/career-page/postings?page=1&limit=100`;
      const response = await axios.get(url);
      const items = response?.data?.data?.items || [];

      setAllJobs(items);

      // Derive filter categories from the unique departments in the postings
      const uniqueDepartments = Array.from(
        new Map(
          items
            .filter((job) => job?.department)
            .map((job) => [job.department.id, job.department])
        ).values()
      );

      setCategories([{ id: "all", name: "View All" }, ...uniqueDepartments]);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterClick = (category) => {
    setActiveFilter(category.id);
    setActivePage(1);
  };

  const handlePageChange = (page) => {
    setActivePage(page);
  };

  const handleOpenApplicationModal = (job) => {
    setSelectedJob(job);
    setModalOpen(true);
  };

  const handleOpenApplicationViewModal = (job) => {
    setSelectedJob(job);
    setModalOpen1(true);
  };

  const handleCloseApplicationModal = () => {
    setModalOpen(false);
    setModalOpen1(false);
    setSelectedJob(null);
  };

  const handleApply = () => {
    setModalOpen1(false);
    setModalOpen(true);
  };

  // Filter jobs by the active department, then paginate client-side
  const filteredJobs =
    activeFilter === "all"
      ? allJobs
      : allJobs.filter((job) => job?.department?.id === activeFilter);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredJobs.length / ITEMS_PER_PAGE)
  );
  const paginatedJobs = filteredJobs.slice(
    (activePage - 1) * ITEMS_PER_PAGE,
    activePage * ITEMS_PER_PAGE
  );

  const getLocationLabel = (job) => {
    const loc = job?.locations?.[0];
    if (!loc) return "";
    return loc.city ? `${loc.city}, ${loc.state}` : loc.name;
  };

  const getEmploymentTypeLabel = (job) => {
    return job?.employment_type ? formatText(job.employment_type) : "";
  };

  return (
    <>
      <OuterGlassBox>
        {/* Filter buttons */}
        <Box
          display="flex"
          flexWrap="nowrap"
          sx={{
            overflowX: "auto",
            pb: 2,
            mb: 3,
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }}
        >
          {categories?.map((category) => (
            <FilterButton
              key={category.id}
              isActive={activeFilter === category.id}
              onClick={() => handleFilterClick(category)}
            >
              {formatText(category?.name)}
            </FilterButton>
          ))}
        </Box>

        {/* Job listings grid */}
        <Grid container spacing={2}>
          {paginatedJobs?.map((job) => (
            <Grid item xs={12} sm={6} key={job.posting_code}>
              <JobCardStyled
                isHovered={hoveredCard === job.posting_code}
                onMouseEnter={() => setHoveredCard(job.posting_code)}
                onMouseLeave={() => setHoveredCard(null)}
                elevation={0}
                onClick={() => handleOpenApplicationViewModal(job)}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "12px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatText(job?.department?.name)}
                  </Typography>
                  <ApplyNowButton
                    endIcon={<NorthEastIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenApplicationModal(job);
                    }}
                    sx={{
                      color: "rgba(255,255,255,1)",
                      fontSize: 16,
                      padding: 0,
                      minWidth: "auto",
                      lineHeight: 1,
                    }}
                  >
                    Apply Now
                  </ApplyNowButton>
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 600,
                    fontSize: "18px",
                    mb: 2,
                  }}
                >
                  {formatText(job?.title)}
                </Typography>

                <Box display={"flex"}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box display="flex" alignItems="center">
                      <AccessTimeIcon
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: 16,
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        {getEmploymentTypeLabel(job)}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <PlaceIcon
                        sx={{
                          color: "rgba(255,255,255,0.7)",
                          fontSize: 16,
                          mr: 0.5,
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "rgba(255,255,255,0.7)" }}
                      >
                        {getLocationLabel(job)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </JobCardStyled>
            </Grid>
          ))}

          {!loading && paginatedJobs.length === 0 && (
            <Grid item xs={12}>
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>
                No job postings found.
              </Typography>
            </Grid>
          )}
        </Grid>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={4}>
            <IconButton
              sx={{
                color: "#FFFFFF",
                border: "1px solid rgba(255,255,255,0.3)",
                mr: 2,
              }}
              onClick={() => {
                if (activePage > 1) {
                  handlePageChange(activePage - 1);
                }
              }}
            >
              <NavigateBeforeIcon />
            </IconButton>

            <Box display="flex" alignItems="center">
              {Array.from({ length: totalPages }, (_, index) => index + 1).map(
                (page) => (
                  <PaginationDot
                    key={page}
                    isActive={activePage === page}
                    onClick={() => handlePageChange(page)}
                    sx={{ cursor: "pointer" }}
                  />
                )
              )}
            </Box>

            <IconButton
              sx={{
                color: "#FFFFFF",
                border: "1px solid rgba(255,255,255,0.3)",
                ml: 2,
              }}
              onClick={() => {
                if (activePage < totalPages) {
                  handlePageChange(activePage + 1);
                }
              }}
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}
      </OuterGlassBox>

      {selectedJob && (
        <JobApplicationModal
          open={modalOpen}
          onClose={handleCloseApplicationModal}
          role={selectedJob?.title}
          time={getEmploymentTypeLabel(selectedJob)}
          location={getLocationLabel(selectedJob)}
          jobId={selectedJob?.posting_code}
          jobCode={selectedJob?.posting_code}
        />
      )}

      {selectedJob && (
        <JobDetailsModal
          open={modalOpen1}
          job={selectedJob}
          onClose={handleCloseApplicationModal}
          onApply={handleApply}
        />
      )}
    </>
  );
};

export default JobsList;